// src/admin/AdminPuppyManager.js
import React, { useEffect, useState, useCallback } from "react";
import RequireAdmin from "../RequireAdmin";
import {
  listAllPuppies,
  listPublicPuppies,
  addPuppy,
  updatePuppy,
  deletePuppy,
} from "../../utils/puppiesAPI";
import "./AdminPuppyManager.css";

/**
 * Premium Admin Manager with create + edit support
 * - Single collection strategy using entryType: "puppy" | "litter"
 * - Dam and Stud available for both types
 * - Inline editor drawer for quick updates
 */
export default function AdminPuppyManager() {
  const empty = {
    entryType: "puppy", // puppy | litter
    name: "",
    status: "Available", // puppies only
    public: true,
    price: "",
    description: "",
    order: 100,
    sex: "",
    color: "",
    dob: "",
    availableDate: "",
    damName: "",     // now available for both types
    studName: "",    // now available for both types
    expectedDate: "",// litters only
  };

  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // filter mode: all | public | hidden
  const [filterMode, setFilterMode] = useState("all");

  // edit drawer state
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      let rows = [];
      if (filterMode === "public") {
        rows = await listPublicPuppies();
      } else {
        rows = await listAllPuppies();
        if (filterMode === "hidden") {
          rows = rows.filter((r) => !r.public);
        }
      }

      // Stable sort by order then name for nicer admin browsing
      rows.sort((a, b) => {
        const ao = typeof a.order === "number" ? a.order : 9999;
        const bo = typeof b.order === "number" ? b.order : 9999;
        if (ao !== bo) return ao - bo;
        return String(a.name || "").localeCompare(String(b.name || ""));
      });

      setEntries(rows);
    } catch (err) {
      console.error(err);
      setErrorMsg("Could not load entries. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [filterMode]);

  useEffect(() => {
    load();
  }, [load]);

  // cleanup preview object URL
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(f ? URL.createObjectURL(f) : "");
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg("");
    try {
      const payload = {
  entryType: form.entryType,
  name: form.name.trim(),
  public: !!form.public,
  description: form.description.trim(),
  order: Number(form.order) || 100,

  // always present fields, use safe defaults
  status:
  form.entryType === "puppy"
    ? (form.status && ["Available","Reserved","Adopted","Hold"].includes(form.status)
        ? form.status
        : "Available")
    : "Available",

  price:
    form.entryType === "puppy" && form.price !== ""
      ? Number(form.price)
      : 0,
  sex: form.entryType === "puppy" ? form.sex || "" : "",
  color: form.color.trim() || "", // works for both puppy & litter
  dob: form.entryType === "puppy" ? form.dob || "" : "",
  availableDate: form.entryType === "puppy" ? form.availableDate || "" : "",
  expectedDate: form.entryType === "litter" ? form.expectedDate || "" : "",

  damName: form.damName.trim() || "",
  studName: form.studName.trim() || "",
};


      const id = await addPuppy({ data: payload, file });
      // reset
      setForm(empty);
      setFile(null);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl("");
      await load();
      alert(`${form.entryType === "puppy" ? "Puppy" : "Litter"} created: ${id}`);
    } catch (err) {
      console.error(err);
      setErrorMsg("Create failed. Please review the inputs and try again.");
    } finally {
      setLoading(false);
    }
  };

  const quickUpdate = async (id, patch) => {
    setLoading(true);
    setErrorMsg("");
    try {
      await updatePuppy(id, patch);
      await load();
    } catch (err) {
      console.error(err);
      setErrorMsg("Update failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete ${row.name}?`)) return;
    setLoading(true);
    setErrorMsg("");
    try {
      await deletePuppy(row.id, row.imageUrl);
      await load();
    } catch (err) {
      console.error(err);
      setErrorMsg("Delete failed.");
    } finally {
      setLoading(false);
    }
  };

  // open editor drawer for a row
  const openEditor = (row) => {
    // Normalize defaults for safety if older docs exist
    const normalized = {
      entryType: row.entryType || "puppy",
      name: row.name || "",
      status: row.status || "Available",
      public: typeof row.public === "boolean" ? row.public : true,
      price: row.price ?? "",
      description: row.description || "",
      order: typeof row.order === "number" ? row.order : 100,
      sex: row.sex || "",
      color: row.color || "",
      dob: row.dob || "",
      availableDate: row.availableDate || "",
      damName: row.damName || "",
      studName: row.studName || "",
      expectedDate: row.expectedDate || "",
    };
    setEditingId(row.id);
    setEditForm(normalized);
  };

  const closeEditor = () => {
    setEditingId(null);
    setEditForm(null);
  };

  const saveEdit = async () => {
  if (!editingId || !editForm) return;
  setEditSaving(true);
  setErrorMsg("");

  try {
    const patch = {
      entryType: editForm.entryType,
      name: editForm.name.trim(),
      public: !!editForm.public,
      description: editForm.description.trim(),
      order: Number(editForm.order) || 100,

      // puppy-only fields
      status: editForm.entryType === "puppy" ? editForm.status : null,
      sex: editForm.entryType === "puppy" ? editForm.sex || null : null,
      dob: editForm.entryType === "puppy" ? editForm.dob || null : null,
      availableDate:
        editForm.entryType === "puppy" ? editForm.availableDate || null : null,

      // shared fields for both puppy & litter
      price: editForm.price !== "" ? Number(editForm.price) : null,
      color: editForm.color.trim() || null,
      damName: editForm.damName.trim() || null,
      studName: editForm.studName.trim() || null,

      // litter-only field
      expectedDate:
        editForm.entryType === "litter" ? editForm.expectedDate || null : null,
    };

    await updatePuppy(editingId, patch);
    await load();
    closeEditor();
  } catch (err) {
    console.error(err);
    setErrorMsg("Save failed.");
  } finally {
    setEditSaving(false);
  }
};


  return (
    <RequireAdmin>
      <div className="adminPuppy">
        <h1>Admin Manager</h1>

        {/* view toggle */}
        <div className="actions" style={{ marginBottom: 16 }}>
          <button
            onClick={() => setFilterMode("all")}
            className={`btn ${filterMode === "all" ? "" : "btnLight"}`}
          >
            Show All
          </button>
          <button
            onClick={() => setFilterMode("public")}
            className={`btn ${filterMode === "public" ? "" : "btnLight"}`}
          >
            Show Public Only
          </button>
          <button
            onClick={() => setFilterMode("hidden")}
            className={`btn ${filterMode === "hidden" ? "" : "btnLight"}`}
          >
            Show Hidden Only
          </button>
        </div>

        {!!errorMsg && (
          <div
            className="panel"
            style={{
              borderColor: "#fca5a5",
              background: "#fff1f2",
              color: "#7f1d1d",
              marginTop: -4,
              marginBottom: 16,
            }}
          >
            {errorMsg}
          </div>
        )}

        {/* create form */}
        <form onSubmit={handleCreate} className="panel">
          <h3>Add Entry</h3>

          {/* EntryType toggle */}
          <div className="toggleRow">
            <label>
              <input
                type="radio"
                name="entryType"
                value="puppy"
                checked={form.entryType === "puppy"}
                onChange={handleChange}
              />
              <span>Puppy</span>
            </label>
            <label>
              <input
                type="radio"
                name="entryType"
                value="litter"
                checked={form.entryType === "litter"}
                onChange={handleChange}
              />
              <span>Upcoming Litter</span>
            </label>
          </div>

          <div className="gridTwo">
            <label>
              Name
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                required
              />
            </label>

            {/* Puppy only fields */}
            {form.entryType === "puppy" && (
              <>
                <label>
                  Status
                  <select
                    name="status"
                    value={form.status}
                    onChange={handleChange}
                  >
                    <option>Available</option>
                    <option>Reserved</option>
                  </select>
                </label>

                <label>
                  Price
                  <input
                    name="price"
                    type="number"
                    value={form.price}
                    onChange={handleChange}
                  />
                </label>

                <label>
                  Color
                  <input
                    name="color"
                    value={form.color}
                    onChange={handleChange}
                  />
                </label>

                <label>
                  Sex
                  <select name="sex" value={form.sex} onChange={handleChange}>
                    <option value="">Select</option>
                    <option>Male</option>
                    <option>Female</option>
                  </select>
                </label>

                <label>
                  Date of Birth
                  <input
                    name="dob"
                    type="date"
                    value={form.dob}
                    onChange={handleChange}
                  />
                </label>

                <label>
                  Available Date
                  <input
                    name="availableDate"
                    type="date"
                    value={form.availableDate}
                    onChange={handleChange}
                  />
                </label>
              </>
            )}

            {/* Litter only field */}
            {form.entryType === "litter" && (
              <label>
                Expected Date
                <input
                  name="expectedDate"
                  type="date"
                  value={form.expectedDate}
                  onChange={handleChange}
                />
              </label>
            )}

            {/* Shared fields for both types */}
            <label>
              Dam Name
              <input
                name="damName"
                value={form.damName}
                onChange={handleChange}
              />
            </label>

            <label>
              Stud Name
              <input
                name="studName"
                value={form.studName}
                onChange={handleChange}
              />
            </label>

            <label>
                  Expected Color
                  <input
                    name="color"
                    value={form.color}
                    onChange={handleChange}
                  />
            </label>

            <label>
                  Price
                  <input
                    name="price"
                    type="number"
                    value={form.price}
                    onChange={handleChange}
                  />
              </label>

            <label className="checkRow">
              <input
                type="checkbox"
                name="public"
                checked={form.public}
                onChange={handleChange}
              />
              Public
            </label>

            <label>
              Upload Image
              <input type="file" accept="image/*" onChange={handleFile} />
            </label>
          </div>

          {previewUrl && (
            <div className="footerRow previewWrap">
              <img
                src={previewUrl}
                alt="Selected"
              />
            </div>
          )}

          <label>
            Description
            <textarea
              name="description"
              rows={3}
              value={form.description}
              onChange={handleChange}
              className="footerRow"
            />
          </label>

          <div className="actions footerRow">
            <button type="submit" disabled={loading} className="btn">
              {loading ? "Saving..." : `Add ${form.entryType}`}
            </button>
          </div>
        </form>

        {/* List */}
        <h3>Current Entries</h3>
        {loading && <div className="panel">Loading…</div>}
        {!loading && entries.length === 0 && <div className="panel">No entries yet.</div>}

        <div className="list">
          {entries.map((p) => {
            const type = p.entryType || "puppy"; // safety default
            return (
              <div key={p.id} className="card">
                <div>
                  {p.imageUrl ? (
                    <img src={p.imageUrl} alt={p.name} className="thumb" />
                  ) : (
                    <div className="noThumb">No image</div>
                  )}
                </div>

                <div className="meta">
  {/* Title */}
  <div className="name">{p.name}</div>

  {/* Badges row */}
  <div className="badges">
    <span className="badge alt">{type}</span>
    {p.public && <span className="badge">Public</span>}
  </div>

  {/* Info */}
  {type === "puppy" ? (
    <>
      <dl className="kv">
        <dt>Status</dt><dd>{p.status}</dd>
        {"price" in p && p.price != null && (
          <>
            <dt>Price</dt><dd>${p.price}</dd>
          </>
        )}
        {p.sex && (
          <>
            <dt>Sex</dt><dd>{p.sex}</dd>
          </>
        )}
        {p.color && (
          <>
            <dt>Color</dt><dd>{p.color}</dd>
          </>
        )}
        {p.dob && (
          <>
            <dt>Date of Birth</dt><dd>{p.dob}</dd>
          </>
        )}
        {p.availableDate && (
          <>
            <dt>Available</dt><dd>{p.availableDate}</dd>
          </>
        )}
        {p.damName && (
          <>
            <dt>Dam</dt><dd>{p.damName}</dd>
          </>
        )}
        {p.studName && (
          <>
            <dt>Stud</dt><dd>{p.studName}</dd>
          </>
        )}

      </dl>
      {p.description && <div>{p.description}</div>}
    </>
  ) : (
    <>
      <dl className="kv">
        <dt>Type</dt><dd>Upcoming Litter</dd>
        {p.damName && (
          <>
            <dt>Dam</dt><dd>{p.damName}</dd>
          </>
        )}
        {p.studName && (
          <>
            <dt>Stud</dt><dd>{p.studName}</dd>
          </>
        )}
        {p.expectedDate && (
          <>
            <dt>Expected</dt><dd>{p.expectedDate}</dd>
          </>
        )}
       
      </dl>
      {p.description && <div>{p.description}</div>}
    </>
  )}
</div>


                <div className="actions">
                  <button
                    className="btn btnLight"
                    onClick={() => quickUpdate(p.id, { public: !p.public })}
                  >
                    Toggle Public
                  </button>

                  {type === "puppy" && (
                    <button
                      className="btn btnLight"
                      onClick={() =>
                        quickUpdate(p.id, {
                          status:
                            p.status === "Available" ? "Reserved" : "Available",
                        })
                      }
                    >
                      Toggle Status
                    </button>
                  )}

                  <button className="btn" onClick={() => openEditor(p)}>
                    Edit
                  </button>

                  <button className="btn btnDanger" onClick={() => handleDelete(p)}>
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Editor Drawer */}
        {!!editForm && (
          <EditDrawer
            form={editForm}
            setForm={setEditForm}
            onClose={closeEditor}
            onSave={saveEdit}
            saving={editSaving}
          />
        )}
      </div>
    </RequireAdmin>
  );
}

/** Inline editor drawer component */
function EditDrawer({ form, setForm, onClose, onSave, saving }) {
  const change = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  return (
    <div
  className="adminPuppyEditOverlay"
  onClick={(e) => {
    if (e.target === e.currentTarget) onClose();
  }}
>
  <div className="panel adminPuppyEditDrawer">

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <h3 style={{ margin: 0 }}>Edit Entry</h3>
          <button className="btn btnLight" onClick={onClose}>Close</button>
        </div>

        {/* Entry type cannot change easily once live, but we allow it for flexibility */}
        <div className="toggleRow" style={{ marginTop: 8 }}>
          <label>
            <input
              type="radio"
              name="entryType"
              value="puppy"
              checked={form.entryType === "puppy"}
              onChange={change}
            />
            <span>Puppy</span>
          </label>
          <label>
            <input
              type="radio"
              name="entryType"
              value="litter"
              checked={form.entryType === "litter"}
              onChange={change}
            />
            <span>Upcoming Litter</span>
          </label>
        </div>

        <div className="gridTwo">
          <label>
            Name
            <input name="name" value={form.name} onChange={change} />
          </label>

          {form.entryType === "puppy" && (
            <>
              <label>
                Status
                <select name="status" value={form.status} onChange={change}>
                  <option>Available</option>
                  <option>Reserved</option>
                </select>
              </label>

              <label>
                Price
                <input
                  name="price"
                  type="number"
                  value={form.price}
                  onChange={change}
                />
              </label>

              <label>
                Color
                <input name="color" value={form.color} onChange={change} />
              </label>

              <label>
                Sex
                <select name="sex" value={form.sex} onChange={change}>
                  <option value="">Select</option>
                  <option>Male</option>
                  <option>Female</option>
                </select>
              </label>

              <label>
                Date of Birth
                <input name="dob" type="date" value={form.dob} onChange={change} />
              </label>

              <label>
                Available Date
                <input
                  name="availableDate"
                  type="date"
                  value={form.availableDate}
                  onChange={change}
                />
              </label>
            </>
          )}

          {form.entryType === "litter" && (
          <>
            <label>
              Expected Date
              <input
                name="expectedDate"
                type="date"
                value={form.expectedDate}
                onChange={change}
              />
            </label>

            <label>
                Price
                <input
                  name="price"
                  type="number"
                  value={form.price}
                  onChange={change}
                />
              </label>

            <label>
              Expected Puppy Color
              <input
                name="color"
                value={form.color}
                onChange={change}
                placeholder="e.g. Light Golden, Red, Mix"
              />
            </label>
          </>
        )}


          {/* Shared for both */}
          <label>
            Dam Name
            <input name="damName" value={form.damName} onChange={change} />
          </label>

          <label>
            Stud Name
            <input name="studName" value={form.studName} onChange={change} />
          </label>

          <label className="checkRow">
            <input
              type="checkbox"
              name="public"
              checked={!!form.public}
              onChange={change}
            />
            Public
          </label>
        </div>

        <label className="footerRow">
          Description
          <textarea
            name="description"
            rows={3}
            value={form.description}
            onChange={change}
          />
        </label>

        <div className="actions footerRow">
          <button className="btn btnLight" onClick={onClose}>
            Cancel
          </button>
          <button className="btn" onClick={onSave} disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
